import {
  Component, OnInit, OnDestroy, Inject, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, ValidatorFn, AbstractControl, ValidationErrors, FormArray } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, filter, Subscription, take } from 'rxjs';
import { PriceListForm, PriceListFormData, PriceListFormResult, PriceType } from 'src/state/price-list-form/state-models/price-list-form';
import { PriceListFormStateService } from 'src/state/price-list-form/service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
// import { MatChipInput, MatChipInputEvent } from '@angular/material/chips';
import { variantCodeValues } from 'src/state/production-price-list/state-models/production-price-condition';
// import { ImportedPriceListVariantDetail, ImportPriceListExcel } from 'src/state/production-price-list/state-models/production-price-list';
import { ProductionPriceListApiService } from 'src/api/services/production-price-list-api.service';
import { DatePipe } from '@angular/common';
import toastConfig from 'src/app/shared/custom-toast-options';
import { CommonStateService, ImportedPriceListVariantDetail } from 'src/state';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

@Component({
  selector: 'cal-price-list-form',
  templateUrl: './price-list-form.component.html',
  styleUrl: './price-list-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceListFormComponent implements OnInit, OnDestroy {

  resources: any = this.commonService.getResources();
  form!: FormGroup;
  variantCtrl = new FormControl<string>('');
  selectedVariants: string[] = [];
  selectedVariantValues: variantCodeValues[] = [];  // This holds key value for all selected variants
  variantSuggestions: variantCodeValues[] = [];
  maxVariants = 6;
  minDate = new Date();
  minDateTo = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })();
  isVariantLoading = false;
  previousVariantSearch = '';
  private get variantComboCtrl(): FormControl<variantCodeValues[]> {
    return this.form.get('variantCombination') as FormControl<variantCodeValues[]>;
  }

  priceTypes: PriceType[] = [];
  isEdit = false;

  subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private stateService: PriceListFormStateService,
    private dialogRef: MatDialogRef<PriceListFormComponent, PriceListFormResult>,
    private importPriceListExcelApiService: ProductionPriceListApiService,
    private commonService: CommonStateService,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: PriceListFormData,
    private cd: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      description: ['', [Validators.required, Validators.maxLength(80)]],
      validFrom: new FormControl({ value: new Date(), disabled: true }),
      validTo: ['', this.validToValidator(() => this.form?.get('validFrom')?.value)],
      variantCombination: this.fb.control<string[]>([]),
      priceListTypeId: [this.data.selectedPriceListTypeId],
      prices: this.fb.array([], Validators.required)
    });

    this.stateService.fetchPriceTypeList();
    this.stateService.fetchVariantCombinations('');

    this.minDate.setDate((new Date()).getDate() + 1);

    this.subscription.add(
      this.stateService.getPriceTypes().pipe(
        filter(types => types.length > 0),
        take(1)).subscribe(types => {
          this.priceTypes = types;
          this.isEdit = !!this.data?.selectedRow;
          Promise.resolve().then(() => {
            if (this.isEdit) {
              this.patchForEdit();
            }
            else {
              this.setupForAdd();
            }
            this.cd.detectChanges();
            // Re-validate validFrom after change detection updates the [min] binding,
            // so a pre-existing past date does not show a matDatepickerMin error.
            this.form.get('validFrom')?.updateValueAndValidity({ emitEvent: false });
          });
        })
    );

    this.subscription.add(
      this.stateService.getVariantSuggestions().subscribe(suggestions => {
        this.variantSuggestions = suggestions;
        this.cd.detectChanges();
      })
    );

    this.subscription.add(
      this.variantCtrl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(text => {
        this.stateService.fetchVariantCombinations(text || '');
      })
    );

    // this.subscription.add(
    //   this.form.get('variantCombination')!.valueChanges.subscribe(

    //     val => { console.log("value chagnes : " + val); this.selectedVariants = val || []; }
    //   )
    // );


  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setupForAdd(): void {
    this.prices.push(this.buildPriceRow());
    const fd = this.form.get('validFrom');
    fd?.enable();
    fd?.setValue(this.tomorrow());
    this.form.get('validTo')?.setValue(null);
  }
  private tomorrow(): Date {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  patchForEdit() {
    const row = this.data?.selectedRow!;
    if (!row) { return; }
    const variantValues: string[] = (row.variantCombination ?? []).map((v: any) => v.value);

    this.selectedVariantValues = [...(row.variantCombination ?? [])];

    this.form.patchValue({
      description: row.description ?? '',
      validFrom: row.validFrom ? new Date(row.validFrom) : '',
      validTo: row.validTo ? new Date(row.validTo) : null,
      variantCombination: variantValues,
    });
    
    this.selectedVariants = variantValues;
    const items = (row.priceType ?? []).map((pt: any) => ({
      id: pt.id,
      name: pt.name,
      priceValue: pt.priceValue
    }));
    this.prices.clear();
    if (!items.length) {
      this.prices.push(this.buildPriceRow());
    }
    else {
      for (const pt of items) {
        const matched = this.priceTypes.find(t => t.id === pt.id) ?? null;
        this.prices.push(this.fb.group(
          {
            selectedType: [matched!, Validators.required],
            priceValue: [pt.priceValue, [Validators.required, Validators.pattern(/^[-]?\d+(\.\d{1,6})?$/)]]
          }
        ));
      }
    };
    const validFromCtrl = this.form.get('validFrom');
    const today = new Date();
    today.setHours(0,0,0,0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // If existing validFrom is a past/today date, set minDate to that date to avoid mat-datepicker min error.
    // User can still only pick tomorrow+ from the calendar (minDate is updated on picker open).
    if (row.validFrom) {
      const from = new Date(row.validFrom);
      from.setHours(0,0,0,0);
      this.minDate = from < tomorrow ? from : tomorrow;
    } else {
      this.minDate = tomorrow;
    }
    // Always enable validFrom so user can select from tomorrow onwards
    validFromCtrl?.enable({emitEvent: false});
    if (this.prices.length > 1) {
      this.prices.controls.forEach(
        ctrl => {
          (ctrl as FormGroup).get('selectedType')?.disable({ emitEvent: false });
          (ctrl as FormGroup).get('priceValue')?.disable({ emitEvent: false });
        });
    }
    // enable
    else {
      this.prices.controls.forEach(ctrl => {
        (ctrl as FormGroup).get('selectedType')?.enable({ emitEvent: false });
        (ctrl as FormGroup).get('priceValue')?.enable({ emitEvent: false });
      })
    }

    
  }

  onFromPickerOpened(): void {
    // When user opens the calendar to pick a new date, enforce tomorrow as minimum
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    this.minDate = tomorrow;
  }

  buildPriceRow(): FormGroup {
    return this.fb.group({
      selectedType: [null, Validators.required],
      priceValue: [null, [Validators.required, Validators.pattern(/^[-]?\d+(\.\d{1,6})?$/)]]
    });
  }

  comparePriceType = (a: PriceType, b: PriceType): boolean => {
    return a && b ? a.id === b.id : false;
  };

  get prices(): FormArray {
    return this.form.get('prices') as FormArray;
  }

  addPriceRow() {
    if (this.prices.length < 6) {
      this.prices.push(this.buildPriceRow());
    }
  }

  removePriceRow(i: number) {
    if (this.prices.length > i) {
      this.prices.removeAt(i);
    }
  }


  // Changed: validTo is now validated against today's date (not validFrom),
  // because validTo can be today or any future date while validFrom is tomorrow+.
  // Previously it checked toDate >= fromDate which caused form invalid when validTo = today and validFrom = tomorrow.
  private validToValidator(getFrom: () => string): ValidatorFn {
    return (control: AbstractControl) => {
      const to = control.value;
      if (!to) {
        return null;
      }
      // Allow validTo to be today or any future date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const toDate = new Date(to);
      toDate.setHours(0, 0, 0, 0);
      return toDate >= today ? null : { min: true };
    };
  }

  private findSuggestionByValue(val: string): variantCodeValues | undefined {
    return this.variantSuggestions.find(v => v.value === val);
  }
  private findSuggestionByKey(key: string): variantCodeValues | undefined {
    return this.variantSuggestions.find(v => v.key === key);
  }

  displayValue(val?: string): string {
    if (!val) return '';
    const found = this.findSuggestionByValue(val);
    return found ? found.value : val;
  }


  private pushUniqueVariant(val: string): void {

    if (!val) return;
    if (this.selectedVariants.includes(val)) return;
    if (this.selectedVariants.length >= this.maxVariants) return;
    const selected = this.findSuggestionByKey(val);
    if (!selected) return;
    this.selectedVariants = [...this.selectedVariants, selected.value];

    this.selectedVariantValues.push(selected);

    if (this.form?.get('variantCombination')) {
      this.form.get('variantCombination')!.setValue(this.selectedVariants);
    }
  }

  addVariantFromInput(): void {

    const typed = (this.variantCtrl.value || '').toString().trim();
    if (!typed) {
      this.variantCtrl.setValue('');
      return;
    }
    const match = this.findSuggestionByValue(typed)
    if (match) this.pushUniqueVariant(match.value);
    this.variantCtrl.setValue('');

  }

  selectVariant(event: MatAutocompleteSelectedEvent): void {

    const chosenValue = event.option.value as string;
    if (!chosenValue) return;
    this.pushUniqueVariant(chosenValue)
    this.variantCtrl.setValue('');
  }

  removeVariant(variant: string): void {

    this.selectedVariants = (this.selectedVariants ?? []).filter(v => v !== variant);
    if (this.form?.get('variantCombination')) {
      this.form.get('variantCombination')!.setValue(this.selectedVariants);
    }

    this.selectedVariantValues = this.selectedVariantValues.filter(v => !(v.value == variant));
  }



  async onSubmit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.getRawValue();
    const priceType = formValue.prices.map((row: any) => ({
      id: row.selectedType.id,
      name: row.selectedType.name,
      priceValue: row.priceValue
    }));

    const variantDetails: ImportedPriceListVariantDetail[] = this.selectedVariantValues.map((variant: variantCodeValues) => {
      return {
        variantClass: "",
        variantFamily: "",
        variantOption: "",
        variantOptionId: variant.key.includes('vo') ? +variant.key.replace('vo', '') : undefined,
        variantFamilyId: variant.key.includes('vf') ? +variant.key.replace('vf', '') : undefined
      };
    });

    // let validFrom = moment(formValue.validFrom, 'YYYY-MM-DD', true);//this.datePipe.transform(formValue.validFrom, 'yyyy-MM-dd');
    // // Set from date to Tomorrow if price change
    // if (this.data.selectedRow && priceType[0]['priceValue'] != this.data.selectedRow.priceType[0].priceValue) {
    //   validFrom = moment(this.minDate, 'YYYY-MM-DD', true);//this.datePipe.transform(this.minDate, 'yyyy-MM-dd');
    // }

    // Parse formValue.validFrom strictly using moment
    let validFromMoment = moment(formValue.validFrom);
    const today = moment().startOf('day');
    const tomorrow = moment().startOf('day').add(1, 'days');

    // Check if price changed
    const priceChanged =
      this.data.selectedRow &&
      priceType[0]['priceValue'] !== this.data.selectedRow.priceType[0].priceValue;

    // If price changed and validFrom is today or in the past (not explicitly set to future), force to tomorrow
    if (priceChanged && (!validFromMoment.isValid() || validFromMoment.isSameOrBefore(today, 'day'))) {
      validFromMoment = tomorrow;
    }

    let validFrom = validFromMoment.format('YYYY-MM-DD');

    console.log('Final validFrom:', validFrom);
    console.log('Final validTo:', formValue.validTo ? this.datePipe.transform(formValue.validTo, 'yyyy-MM-dd') : null);

    const priceCondition = [{
      priceListId: this.data.selectedPriceListTypeId,
      componentType: this.data.selectedPriceList.componentType.code,
      condition: this.data.selectedRow?.id || 0,
      conditionDescription: formValue.description,
      price: priceType[0]['priceValue'],
      priceType: priceType[0]['name'],
      validFrom: validFrom,
      validTo: formValue.validTo ? this.datePipe.transform(formValue.validTo, 'yyyy-MM-dd') : null,
      errorMessage: [],
      importedPriceListVariantDetails: variantDetails,
      market: this.data.selectedPriceList.market.name
    }];

    debugger;
    const verify = await this.importPriceListExcelApiService.verify(priceCondition);

    let result;
    if (verify.data.productionPriceListImport.verify[0]?.errorMessage.length == 0) {
      result = await this.importPriceListExcelApiService.confirm(priceCondition);
      this.toastr.success(this.resources.ImportedPriceListDataSavedSuccessfully, this.resources.Success, toastConfig)
    } else {
      const errors = this.resources.Error + "\n" + verify.data.productionPriceListImport.verify[0].errorMessage;
      this.toastr.error(errors, this.resources.Error, toastConfig);
    }

    this.dialogRef.close('submitted');
  }

  onCancel(): void {
    this.dialogRef.close('cancelled');
  }

  searchVariants(query: string): void {
    if (this.isVariantLoading || query === this.previousVariantSearch) return;

    this.isVariantLoading = true;
    this.previousVariantSearch = query;

    this.stateService.fetchVariantCombinations(query || '').finally(() => {
      this.isVariantLoading = false;
    });
  }
  // Handle Enter key
  onEnterKey(): void {
    const value = this.variantCtrl.value;
    if (value && this.shouldSearch(value)) {
      this.searchVariants(value);
    }
  }

  // Handle paste event
  onPasteEvent(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text')?.trim();
    if (pastedText && this.shouldSearch(pastedText)) {
      // Small delay to wait for paste to populate input
      setTimeout(() => {
        this.searchVariants(this.variantCtrl.value || '');
      });
    }
  }
  // Utility function to avoid redundant/invalid queries
  shouldSearch(value: string | null): boolean {
    const trimmed = value?.trim();
    return !!trimmed &&
      trimmed.length >= 2 &&
      !this.selectedVariants.includes(trimmed);
  }


}







